import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const AppointmentLetterEditor = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedUser, setSelectedUser] = useState(location.state?.user || null);
  const [profile, setProfile] = useState(null);
  const [appointmentContent, setAppointmentContent] = useState("");
  const [hasSentLetter, setHasSentLetter] = useState(false);
  const [actionType, setActionType] = useState("send");

  const getObjectId = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value?._id || null;
    return value;
  };

  const buildAppointmentHtml = (userName, companyName) => {
    return `Date: ${new Date().toLocaleDateString()}\n\nTo,\n${userName || "Employee"}\n\nSubject: Appointment Letter\n\nDear ${userName || "Employee"},\n\nFollowing your acceptance of our employment offer, we are pleased to confirm your appointment with ${
      companyName || "our organization"
    }. This letter confirms the commencement of your employment, subject to the terms of your accepted offer and the company's policies.\n\nPlease complete the joining formalities, submit the required documents, and report to Human Resources on your joining date. Your duties, reporting relationship, working hours, compensation, and other employment conditions will be governed by your offer letter and applicable company policies.\n\nWe look forward to your contribution and wish you a successful association with us.\n\nSincerely,\nFor ${
      companyName || "our organization"
    }\nAuthorized Signatory`;
  };

  useEffect(() => {
    const fetchEditorData = async () => {
      if (!token) {
        toast.error("No authentication token found");
        setLoading(false);
        return;
      }

      try {
        const [profileRes, usersRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/establishment/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          selectedUser
            ? Promise.resolve({ data: { activeUsers: [selectedUser] } })
            : axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/establishment/active-users`, {
                headers: { Authorization: `Bearer ${token}` },
              }),
        ]);

        const establishment = profileRes?.data || null;
        setProfile(establishment);

        const user =
          selectedUser ||
          (usersRes?.data?.activeUsers || []).find((u) => u?._id === userId) ||
          null;

        if (!user) {
          toast.error("User not found for appointment letter");
          setLoading(false);
          return;
        }

        setSelectedUser(user);
        setAppointmentContent(buildAppointmentHtml(user?.full_Name, establishment?.name));

        const hiredId = getObjectId(user?.hired);
        if (hiredId) {
          const existingRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/${hiredId}?letterType=appointment`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const existingLetter = existingRes?.data?.offerLetter;
          if (existingLetter?.letterContent?.content) {
            const plainText = String(existingLetter.letterContent.content)
              .replace(/<br\s*\/?>/gi, "\n")
              .replace(/<\/p>/gi, "\n")
              .replace(/<[^>]*>/g, "");
            setAppointmentContent(plainText);
            if (existingLetter.status === "sent" || existingLetter.status === "accepted") {
              setHasSentLetter(true);
            }
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load appointment letter");
      } finally {
        setLoading(false);
      }
    };

    fetchEditorData();
  }, [token, userId]);

  const handleSendAppointment = async (type = "send") => {
    try {
      setActionType(type);
      const hiredId = getObjectId(selectedUser?.hired);
      const hiringId = getObjectId(selectedUser?.hired?.hiring_id);

      if (!hiredId || !hiringId) {
        toast.error("Related hiring details are missing for this user.");
        return;
      }

      if (!profile?._id) {
        toast.error("Establishment profile not found.");
        return;
      }

      setSending(true);
      const payload = {
        establishmentId: profile._id,
        userId: selectedUser._id,
        hiredId,
        hiringId,
        sendEmail: type === "send",
        letterType: "appointment",
        letterContent: {
          header: {
            companyName: profile?.name || "",
            address: profile?.address || "",
            email: profile?.email || "",
            phone: profile?.contact || "",
          },
          title: "Appointment Letter",
          content: appointmentContent
            ? appointmentContent.replace(/\n/g, "<br/>")
            : buildAppointmentHtml(selectedUser?.full_Name, profile?.name).replace(/\n/g, "<br/>"),
          signatures: {
            company: {
              title: `FOR ${profile?.name || ""}`,
              designation: "Authorized Signatory",
            },
            employee: {
              name: selectedUser?.full_Name || "",
              designation: "Receiving Signature",
            },
          },
        },
        documentStyle: {
          logoPosition: { x: 20, y: 20 },
          logoSize: { width: 150, height: 100 },
          signaturePosition: { x: 0, y: 0 },
          signatureSize: { width: 150, height: 80 },
        },
        assets: {
          logo: profile?.logo || null,
          signature: profile?.signature || null,
        },
        status: "sent",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.success) {
        toast.success(
          response.data.message ||
            (type === "update"
              ? "Appointment letter updated successfully"
              : "Appointment letter sent successfully")
        );
        setHasSentLetter(true);
      } else {
        toast.error(
          response?.data?.message ||
            (type === "update"
              ? "Failed to update appointment letter"
              : "Failed to send appointment letter")
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (type === "update"
            ? "Failed to update appointment letter"
            : "Failed to send appointment letter")
      );
    } finally {
      setSending(false);
      setActionType("send");
    }
  };

  if (loading) {
    return <div className="text-center mt-4">Loading appointment letter...</div>;
  }

  if (!selectedUser) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">User data not found.</div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate("/establisment_dashboard/appointment-letter")}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 py-md-4">
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Appointment Letter - {selectedUser?.full_Name || "User"}
          </h5>
        </div>
        <div className="card-body">
          <label className="form-label fw-semibold">Edit Appointment Letter</label>
          <textarea
            className="form-control"
            rows={12}
            value={appointmentContent}
            onChange={(e) => setAppointmentContent(e.target.value)}
          />

          <div className="d-flex justify-content-end mt-3">
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate("/establisment_dashboard/appointment-letter")}
            >
              Back
            </button>
          </div>

          <div className="mt-4 border rounded p-3">
            <h6 className="fw-semibold mb-2">
              {hasSentLetter ? "Sent Appointment Letter" : "Appointment Letter Preview"}
            </h6>
            <div style={{ whiteSpace: "pre-wrap" }}>{appointmentContent}</div>

            <div className="d-flex justify-content-end mt-3">
              <button
                className="btn btn-success me-2"
                onClick={() => handleSendAppointment("send")}
                disabled={sending}
              >
                {sending && actionType === "send" ? "Sending..." : "Send Appointment Letter"}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSendAppointment("update")}
                disabled={sending}
              >
                {sending && actionType === "update"
                  ? "Updating..."
                  : "Update Appointment Letter"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentLetterEditor;
